import { Link } from '@tanstack/react-router';
import type { AnyRouter, LinkComponentProps, RegisteredRouter } from '@tanstack/react-router';
import { ROW_LINK_ANCHOR_CLASSNAME } from '@ooh/ui/components/data-table';

type RowDetailLinkProps<
  TRouter extends AnyRouter = RegisteredRouter,
  TFrom extends string = string,
  TTo extends string | undefined = undefined,
  TMaskFrom extends string = TFrom,
  TMaskTo extends string = '',
> = LinkComponentProps<'a', TRouter, TFrom, TTo, TMaskFrom, TMaskTo> & {
  ariaLabel: string;
  testId: string;
};

/**
 * Shared `DataTable` `renderRowLink` cell — a `Link` styled via
 * `ROW_LINK_ANCHOR_CLASSNAME` (the stretched-link overlay anchor). Used by the
 * media-units catalog and the offers list so both rows open their detail page
 * via the same accessible per-row link instead of a `<tr onClick>` handler.
 * Renders no visible glyph (the anchor's accessible name comes entirely from
 * `ariaLabel`) — earlier revisions rendered a `ChevronRight` icon here, which
 * read as a stray, oddly-positioned arrow floating in its own column on every
 * list. The row itself stays fully clickable/keyboard-focusable via the
 * `::after` stretched-link overlay; only the visible glyph was removed.
 *
 * Renders a plain `Link` directly (not via TanStack Router's `createLink`)
 * so it keeps working with every existing test file's `Link`-only router
 * mock — `createLink`'s real implementation always renders the framework's
 * own internal `Link`, bypassing a test's mocked `Link` and needing a real
 * `RouterProvider`. `RowDetailLink` mirrors `Link`'s own generic signature
 * (`RowDetailLinkProps` is generic over the same `TRouter`/`TFrom`/`TTo`/
 * `TMaskFrom`/`TMaskTo` parameters as `LinkComponentProps`) instead of
 * widening `to`/`params` to plain `string`/`Record<string, string>`. This
 * preserves per-call-site literal inference: TypeScript still validates each
 * call site's `params` shape against its own literal `to` string through
 * this wrapper — a typo'd route or a mismatched param key fails to compile
 * here exactly as it would calling `Link` directly (a plain, non-generic
 * `React.ComponentProps<typeof Link>` alias does NOT work here — it forces
 * `Link`'s generics to their defaults, which rejects every valid call site).
 */
export function RowDetailLink<
  TRouter extends AnyRouter = RegisteredRouter,
  const TFrom extends string = string,
  const TTo extends string | undefined = undefined,
  const TMaskFrom extends string = TFrom,
  const TMaskTo extends string = '',
>({
  ariaLabel,
  testId,
  ...linkProps
}: RowDetailLinkProps<TRouter, TFrom, TTo, TMaskFrom, TMaskTo>) {
  // Forwarding `linkProps` from one generic-over-route component
  // (`RowDetailLink<TRouter, TFrom, TTo, ...>`) into another with the
  // identical generic shape (`Link`) hits a known TypeScript limitation:
  // TS cannot prove a generic type is assignable to itself across two
  // independently-instantiated generic signatures (microsoft/TypeScript#28884).
  // The public, checked contract that call sites see is `RowDetailLinkProps`
  // above, which IS the real generic `LinkComponentProps` shape — this cast
  // only bridges the internal forwarding to the concrete `Link` element and
  // does not widen what callers of `RowDetailLink` are allowed to pass.
  const linkComponentProps = linkProps as unknown as LinkComponentProps<'a'>;
  return (
    <Link
      {...linkComponentProps}
      aria-label={ariaLabel}
      className={ROW_LINK_ANCHOR_CLASSNAME}
      data-testid={testId}
    />
  );
}
